import Api from './Api'
import LongPolling from './LongPolling'

export default class ChatClient {

  static BOT_USER = {
    "id": 0,
    "name": "bot",
    "role": "bot"
  }

  /*
   * @param authToken 認証トークン
   * @param roomId 接続先ルームID
   * @param roomAccessToken 接続先ルーム認証トークン
   */
  static create (authToken, roomId, roomAccessToken) {
    let api = new Api(authToken);
    api.roomAccessToken = roomAccessToken;
    return new ChatClient(api, roomId);
  }

    /**
     * @param api API呼出に使用するApiインスタンス
     * @param roomId 接続するルームID
     */
    constructor (api, roomId) {
    this.api = api;
    this.roomId = roomId;
    this.messageLongPolling   = new LongPolling(api, `/rooms/${roomId}/messages`);
    this.roomStateLongPolling = new LongPolling(api, `/rooms/${roomId}/state`);

    this.messageFetchHandler = null;
    this.roomStateFetchHandler = null;
    this.messageErrorHandler = null;
    this.roomStateErrorHandler = null;
    this.isWaitingResponse = false;
    };

  set sender (newSender) {
    this._sender = {
      id: newSender.id,
      name: newSender.name,
      role: newSender.role
    }
  }

    set onFetchMessage (handler) {
    this.messageFetchHandler = handler;
    this.messageLongPolling.onFetch = handler;
    }

  set onFetchRoomState (handler) {
    this.roomStateFetchHandler = handler;
    this.roomStateLongPolling.onFetch = handler;
  }

  set onErrorMessage (handler) {
    this.messageErrorHandler = handler;
    this.messageLongPolling.onError = handler;
  }

  set onErrorRoomState (handler) {
    this.roomStateErrorHandler = handler;
    this.roomStateLongPolling.onError = handler;
  }

  /*
   * ルームID変更してロングポーリングを再起動
   */
  reset (roomId) {
    console.log(`Reset chat client: ${roomId}`)

    let restartFetchMessage = this.messageLongPolling.isStarted
    let restartFetchState   = this.roomStateLongPolling.isStarted

    this.stopFetch()

    this.roomId = roomId
    this.messageLongPolling   = new LongPolling(this.api, `/rooms/${roomId}/messages`);
    this.roomStateLongPolling = new LongPolling(this.api, `/rooms/${roomId}/state`);

    this.messageLongPolling.onFetch = this.messageFetchHandler
    this.roomStateLongPolling.onFetch = this.roomStateFetchHandler

    this.messageLongPolling.onError = this.messageErrorHandler
    this.roomStateLongPolling.onError = this.roomStateErrorHandler

    if (restartFetchMessage) {
      this.startFetchMessage()
    }
    if (restartFetchState) {
      this.startFetchRoomState()
    }
  }

  /*
   * ルーム内メッセージ取得開始 (ロングポーリング)
   */
  startFetchMessage () {
    this.messageLongPolling.start();
    }

  /*
   * ルーム状態取得開始 (ロングポーリング)
   */
  startFetchRoomState () {
    this.roomStateLongPolling.start();
  }

    /**
     * ロングポーリングを停止します。
     */
  stopFetch () {
    this.messageLongPolling.stop();
    this.roomStateLongPolling.stop();
    }

  /*
   * メッセージ送信
   */
  sendMessage (params) {
    return this.sendMessageAs (params, this._sender)
  }

  sendMessageAs (params, sender) {
    let message = {
      version: '1.0',
      type: params.messageType,
      sender: sender,
      context: params.context,
      content: params.content
    };
    return this.api.sendRoomMessage(this.roomId, message);
  }

  /*
   * Textメッセージ送信
   */
  sendText (text) {
    return this.sendMessage({
      context: 'initial',
            messageType: 'Text',
            content: {
                text: ChatClient.sanitize(text)
            }
    });
  }

  /*
   * 未読表示用Textメッセージ送信
   */
  unreadText () {
    return this.api.unreadsendMessage (this.roomId)
  }

  sendQuestion (question, category, productCode) {
    this.isWaitingResponse = true
    return this.sendMessage({
      context: 'question',
      messageType: 'Text',
      content: {
        text: question,
        category: category,
        query: question,
        product_code: productCode
      }
    })
  }

  sendProductCategory (category) {
    this.isWaitingResponse = true
    return this.sendMessage({
      context: 'set-product-category',
            messageType: 'Text',
            content: {
                text: ChatClient.sanitize(category.label),
        question_category: category.value
            }
    });
  }

  sendTroubleCategory(troubleCategory, productCategory) {
    this.isWaitingResponse = true
    return this.sendMessage({
      context: 'set-trouble-category',
      messageType: 'Text',
      content: {
        text: ChatClient.sanitize(troubleCategory.label),
        question_category: troubleCategory.value,
        product_category:productCategory
      },
    });
  }

  /*
   * ButtonListメッセージ送信
   */
  sendButtonList (text, buttonType, buttons) {
    return this.sendMessage({
      context: 'initial',
            messageType: 'ButtonList',
            content: {
                text: ChatClient.sanitize(text),
        type: buttonType,
        buttons: buttons
            }
    });
  }

  /*
   * ButtonClickメッセージ送信
   */
  sendButtonClick (buttonType, button) {
    return this.sendMessage({
      context: 'initial',
            messageType: 'ButtonClick',
            content: {
        type: buttonType,
        button: button
            }
    });
  }

  /*
   * ProductSelectメッセージ送信
   */
  sendProductSelect (id, name) {
    return this.sendMessage({
      context: 'product-select',
      messageType: 'ProductSelect',
      content: {
        product_id: id,
        product_name: name
      }
    })
  }

  /*
   * ProductCompareメッセージ送信
   */
  sendProductCompare (text, products, specs) {
    return this.sendMessage({
      context: 'product-compare',
      messageType: 'ProductCompare',
      content: {
        text,
        products,
        specs
      }
    })
  }

  sendProductCode (productCode, category) {
    this.isWaitingResponse = true
    const text = (productCode === '' ? 'わからない' : productCode)
    return this.sendMessage({
      context: 'set-product-code',
      messageType: 'Text',
      content: {
        text: text,
        product_code: productCode,
        question_category: category
      }
    })
  }

  /*
   * Textメッセージ送信
   */
  sendAnswerFeedback (text, isResolved) {
    this.isWaitingResponse = true
    return this.sendMessage({
      context: 'answer-feedback',
            messageType: 'Text',
            content: {
                text: ChatClient.sanitize(text),
        is_resolved: isResolved
            }
    });
  }


  /*
   * Textメッセージ送信(固定FAQ回答)
   */
  sendAnswerFrequentlyQuestionFeedback (text, isResolved) {
    this.isWaitingResponse = true
    return this.sendMessage({
      context: 'answer-feedback-frequently-question',
            messageType: 'Text',
            content: {
                text: ChatClient.sanitize(text),
        is_resolved: isResolved
            }
    });
  }

  /*
   * オペレータ呼び出しメッセージ送信
   */
  sendOperatorCall (text, room) {
    this.api.updateRoomState(room.id, 'bot_hung_up', null)
    this.isWaitingResponse = true
    return this.sendMessage({
      context: 'call-operator',
      messageType: 'Text',
      content: {
        text: ChatClient.sanitize(text)
      }
    })
  }

  /*
   * Linkメッセージ送信
   */
  sendLink (text, url, linkText) {
    return this.sendMessage({
      context: 'initial',
      messageType: 'Link',
      content: {
        text,
        url,
        link_text: linkText
      }
    })
  }

  /**
   * FAQメッセージ送信
   */
  sendFAQ (text, faq) {
    this.sendFAQs(text, [faq])
  }

  sendRequestFrequentlyFaqChatbot (text, category, troubleCategory) {
    this.isWaitingResponse = true
    this.sendMessage({
      context: 'request-frequently-faq-chatbot',
      messageType: 'Text',
      content: {
        text: text,
        question_category: category,
        trouble_category: troubleCategory
      }
    })
  }

  sendRequestFaqChatbot (text) {
    this.isWaitingResponse = true
    this.sendMessage({
      context: 'request-faq-chatbot',
      messageType: 'Text',
      content: {
        text: text
      }
    })
  }

  /**
   * FAQメッセージ送信
   */
  sendFAQs (text, faqs) {
    return this.sendMessage({
      context: 'initial',
            messageType: 'FAQ',
      content: {
        text: text,
        faqs: faqs.map((faq) => {
          return {
            title: faq.title,
            content: faq.content
          }
        })
      }
    })
  }

  /*
   * Fileメッセージ送信
   */
  sendFile (fileId, accessToken, file) {
    return this.sendMessage({
      context: 'initial',
      messageType: 'File',
      content: {
        room_id: this.roomId,
        file_id: fileId,
        access_token: accessToken,
        file: {
          name: file.name,
          type: file.type,
          size: file.size
        }
      }
    })
  }

  isClosed () {
    return this.messageLongPolling.stopRequested || this.roomStateLongPolling.stopRequested
  }

  static sanitize (text) {
    if (!text) return '';
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/`/g, '&#96');
  }

}



// WEBPACK FOOTER //
// ./app-common-vue/src/lib/ChatClient.js